import { EventEmitter } from '../../core/Event';
import type { Random } from '../../core/Random';
import type { BoardGameEvent } from './BoardEvents';
import type { BoardConfig, BoardTile } from './BoardTypes';

export interface BoardModelConfig extends BoardConfig {
  startingBalance: number;
}

/**
 * Authoritative state for a board-game session: player position, balance,
 * tile roster.
 *
 * <p>The model owns the dice roll + position transition + balance credit;
 * the host scene owns animation timing + tile-effect dispatch. Roll a die
 * via {@link rollDie} (returns the value + emits {@code roll-resolved}),
 * then animate the token and call {@link advance} per hop. Each
 * {@link advance} emits {@code position-changed} + {@code tile-activated}
 * + {@code balance-changed} (when the tile awards coins).
 *
 * <p>Lifted-in-spirit from cs-core's {@code BoardGameModel}.
 */
export class BoardModel {
  public readonly events = new EventEmitter<BoardGameEvent>();
  public readonly tiles: readonly BoardTile[];
  public readonly rollMin: number;
  public readonly rollMax: number;

  private _balance: number;
  private _position = 0;

  constructor(config: BoardModelConfig) {
    if (config.tiles.length < 2) {
      throw new Error('BoardModel requires at least 2 tiles');
    }
    this.tiles = config.tiles;
    this.rollMin = config.rollMin ?? 1;
    this.rollMax = config.rollMax ?? 6;
    if (this.rollMax < this.rollMin) {
      throw new Error(`rollMax (${this.rollMax}) must be >= rollMin (${this.rollMin})`);
    }
    this._balance = config.startingBalance;
  }

  get balance(): number {
    return this._balance;
  }

  get position(): number {
    return this._position;
  }

  get currentTile(): BoardTile {
    return this.tiles[this._position]!;
  }

  /** Pull a random die roll. Emits {@code roll-started} synchronously
   * before the result is known (so animations can start), then
   * {@code roll-resolved} with the value. */
  rollDie(rng: Random): number {
    this.events.emit({ kind: 'roll-started' });
    const value = rng.nextIntBetween(this.rollMin, this.rollMax + 1);
    this.events.emit({ kind: 'roll-resolved', value });
    return value;
  }

  /**
   * Move the player {@code steps} tiles forward (wraps around the tile
   * array), activate the landed tile, credit any reward. Emits
   * {@code position-changed}, then {@code balance-changed} (if reward > 0),
   * then {@code tile-activated}, then optionally {@code goal-reached}.
   *
   * <p>Hosts that animate hop-by-hop should call this with {@code 1} per
   * hop. Hosts that prefer a single slide call with the full roll value.
   */
  advance(steps: number): BoardTile {
    if (steps <= 0) return this.currentTile;
    const before = this._position;
    const tileCount = this.tiles.length;
    this._position = (this._position + steps) % tileCount;
    const tile = this.tiles[this._position]!;
    this.events.emit({
      kind: 'position-changed',
      before,
      after: this._position,
      steps,
    });
    if (tile.reward > 0) {
      const balBefore = this._balance;
      this._balance += tile.reward;
      this.events.emit({
        kind: 'balance-changed',
        before: balBefore,
        after: this._balance,
        reason: tile.kind === 'finish' ? 'goal-reached' : 'tile-reward',
      });
    }
    this.events.emit({ kind: 'tile-activated', tileIndex: this._position, tile });
    if (tile.kind === 'finish') {
      this.events.emit({ kind: 'goal-reached', tileIndex: this._position });
    }
    return tile;
  }

  /** Reset position to tile 0. Doesn't change balance. Emits
   * {@code position-changed}; does NOT activate tile 0 (that ran when
   * the model was first constructed at position 0). */
  resetPosition(): void {
    if (this._position === 0) return;
    const before = this._position;
    this._position = 0;
    this.events.emit({ kind: 'position-changed', before, after: 0, steps: 0 });
  }

  /** Direct balance grant — used for session-bonus payouts not tied to
   * a specific tile activation. */
  credit(amount: number, reason: 'session-grant' = 'session-grant'): void {
    if (amount <= 0) return;
    const before = this._balance;
    this._balance += amount;
    this.events.emit({
      kind: 'balance-changed',
      before,
      after: this._balance,
      reason,
    });
  }

  dispose(): void {
    this.events.clear();
  }
}
